'use client';

import { Button } from '@govinity/ui/components/ui/button';
import { useToast } from '@govinity/ui/hooks/use-toast';
import type { PublicSpeakerUserSignup } from '@govinity/validation/public';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MicOff } from 'lucide-react';
import { useTranslations } from 'next-intl';

import api from '@/lib/api';
import { useConfirmationStore } from '@/stores/confirmationStore';
import { publicSpeakerUserSignupsQueryKey } from './helpers';
import { useGovernmentContext } from '../../../context';
import { useMeetingContext } from '../meeting-context';

type SignupListProps = {
  records: PublicSpeakerUserSignup[];
};

export const SignupList = ({ records }: SignupListProps) => {
  const { government } = useGovernmentContext();
  const { meeting } = useMeetingContext();
  const t = useTranslations('PortalPublicSpeakerSignup');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const showConfirmation = useConfirmationStore((state) => state.showConfirmation);

  const {
    mutate: withdraw,
    isPending: isWithdrawing,
    variables: withdrawingSpeakerId,
  } = useMutation({
    mutationFn: (speakerId: number) =>
      api.governments.publicSpeakers(government.code).withdrawUserSignup(meeting.id, speakerId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: publicSpeakerUserSignupsQueryKey(government.code, meeting.id),
      });
      toast({ title: t('withdrawnToast') });
    },
    onError: (mutationError) => {
      toast({
        title: t('withdrawErrorTitle'),
        description: mutationError instanceof Error ? mutationError.message : undefined,
        variant: 'destructive',
      });
    },
  });

  const handleWithdraw = (speakerId: number, entryName: string) => {
    showConfirmation({
      title: t('withdrawConfirmTitle'),
      description: t('withdrawConfirmDescription', { name: entryName }),
      confirmText: t('withdrawConfirmText'),
      confirmVariant: 'destructive',
      onConfirm: () => withdraw(speakerId),
    });
  };

  return (
    <ul className="space-y-2">
      {records.map(({ id, section }) => (
        <li
          key={id}
          className="flex items-center justify-between gap-3 border-b py-1 last:border-b-0"
        >
          <span className="text-sm font-medium">{section.name}</span>
          <Button
            aria-label={t('withdrawAriaLabel', { name: section.name })}
            disabled={isWithdrawing}
            onClick={() => handleWithdraw(id, section.name)}
            size="xs"
            variant="destructive-outline"
          >
            <MicOff aria-hidden="true" />
            {isWithdrawing && withdrawingSpeakerId === id
              ? t('withdrawingButton')
              : t('withdrawButton')}
          </Button>
        </li>
      ))}
    </ul>
  );
};
